/**
 * Patients Redux Slice
 * Manages patient state
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PatientsService } from '../../services/patients.service';
import type {
  Patient,
  PatientListResponse,
  PatientQueryParams,
  CreatePatientData,
  UpdatePatientData,
} from '../../types/patient.types';

/**
 * Patients State Interface
 */
interface PatientsState {
  patients: Patient[];
  currentPatient: Patient | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  searchResults: Patient[];
  isSearching: boolean;
}

/**
 * Initial State
 */
const initialState: PatientsState = {
  patients: [],
  currentPatient: null,
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  isLoading: false,
  error: null,
  searchResults: [],
  isSearching: false,
};

/**
 * Async Thunks
 */

// Get all patients
export const getPatientsAsync = createAsyncThunk<
  PatientListResponse,
  PatientQueryParams | undefined,
  { rejectValue: string }
>(
  'patients/getPatients',
  async (params, { rejectWithValue }) => {
    try {
      const response = await PatientsService.getPatients(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch patients.'
      );
    }
  }
);

// Get patient by ID
export const getPatientByIdAsync = createAsyncThunk<
  Patient,
  string,
  { rejectValue: string }
>(
  'patients/getPatientById',
  async (id, { rejectWithValue }) => {
    try {
      const patient = await PatientsService.getPatientById(id);
      return patient;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch patient.'
      );
    }
  }
);

// Create patient
export const createPatientAsync = createAsyncThunk<
  Patient,
  CreatePatientData,
  { rejectValue: string }
>(
  'patients/createPatient',
  async (data, { rejectWithValue }) => {
    try {
      const patient = await PatientsService.createPatient(data);
      return patient;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create patient.'
      );
    }
  }
);

// Update patient
export const updatePatientAsync = createAsyncThunk<
  Patient,
  { id: string; data: UpdatePatientData },
  { rejectValue: string }
>(
  'patients/updatePatient',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const patient = await PatientsService.updatePatient(id, data);
      return patient;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update patient.'
      );
    }
  }
);

// Delete patient
export const deletePatientAsync = createAsyncThunk<
  { message: string; id: string },
  string,
  { rejectValue: string }
>(
  'patients/deletePatient',
  async (id, { rejectWithValue }) => {
    try {
      const response = await PatientsService.deletePatient(id);
      return { ...response, id };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete patient.'
      );
    }
  }
);

// Search patients
export const searchPatientsAsync = createAsyncThunk<
  Patient[],
  string,
  { rejectValue: string }
>(
  'patients/searchPatients',
  async (query, { rejectWithValue }) => {
    try {
      const patients = await PatientsService.searchPatients(query);
      return patients;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to search patients.'
      );
    }
  }
);

// Get patient history
export const getPatientHistoryAsync = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>(
  'patients/getPatientHistory',
  async (id, { rejectWithValue }) => {
    try {
      const history = await PatientsService.getPatientHistory(id);
      return history;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch patient history.'
      );
    }
  }
);

// Bulk upload patients
export const bulkUploadPatientsAsync = createAsyncThunk<
  { message: string; count: number },
  File,
  { rejectValue: string }
>(
  'patients/bulkUpload',
  async (file, { rejectWithValue }) => {
    try {
      const response = await PatientsService.bulkUpload(file);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to upload patients.'
      );
    }
  }
);

/**
 * Patients Slice
 */
const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    // Set current patient
    setCurrentPatient: (state, action: PayloadAction<Patient | null>) => {
      state.currentPatient = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear patients state
    clearPatients: (state) => {
      state.patients = [];
      state.currentPatient = null;
      state.total = 0;
      state.page = 1;
      state.limit = 20;
      state.totalPages = 0;
      state.error = null;
      state.searchResults = [];
    },

    // Clear search results
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
  },
  extraReducers: (builder) => {
    // Get Patients
    builder
      .addCase(getPatientsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPatientsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patients = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.error = null;
      })
      .addCase(getPatientsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch patients';
      });

    // Get Patient By ID
    builder
      .addCase(getPatientByIdAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPatientByIdAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPatient = action.payload;
        state.error = null;
      })
      .addCase(getPatientByIdAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch patient';
      });

    // Create Patient
    builder
      .addCase(createPatientAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPatientAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patients.unshift(action.payload);
        state.total += 1;
        state.error = null;
      })
      .addCase(createPatientAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create patient';
      });

    // Update Patient
    builder
      .addCase(updatePatientAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePatientAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.patients.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.patients[index] = action.payload;
        }
        if (state.currentPatient?.id === action.payload.id) {
          state.currentPatient = action.payload;
        }
        state.error = null;
      })
      .addCase(updatePatientAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update patient';
      });

    // Delete Patient
    builder
      .addCase(deletePatientAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePatientAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patients = state.patients.filter((p) => p.id !== action.payload.id);
        state.total -= 1;
        if (state.currentPatient?.id === action.payload.id) {
          state.currentPatient = null;
        }
        state.error = null;
      })
      .addCase(deletePatientAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete patient';
      });

    // Search Patients
    builder
      .addCase(searchPatientsAsync.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(searchPatientsAsync.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload;
        state.error = null;
      })
      .addCase(searchPatientsAsync.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload || 'Failed to search patients';
      });

    // Bulk Upload
    builder
      .addCase(bulkUploadPatientsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUploadPatientsAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(bulkUploadPatientsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to upload patients';
      });
  },
});

// Actions
export const { setCurrentPatient, clearError, clearPatients, clearSearchResults } =
  patientsSlice.actions;

// Selectors
export const selectPatients = (state: { patients: PatientsState }) => state.patients;
export const selectPatientsList = (state: { patients: PatientsState }) => state.patients.patients;
export const selectCurrentPatient = (state: { patients: PatientsState }) => state.patients.currentPatient;
export const selectPatientsTotal = (state: { patients: PatientsState }) => state.patients.total;
export const selectPatientsPage = (state: { patients: PatientsState }) => state.patients.page;
export const selectPatientsLoading = (state: { patients: PatientsState }) => state.patients.isLoading;
export const selectPatientsError = (state: { patients: PatientsState }) => state.patients.error;
export const selectSearchResults = (state: { patients: PatientsState }) => state.patients.searchResults;
export const selectIsSearching = (state: { patients: PatientsState }) => state.patients.isSearching;

// Reducer
export default patientsSlice.reducer;
























